import BaseUISystem from '../BaseUISystem';
import { PopupID } from '../../../State/UI/PopupID';
import BaseMenu from 'System/UI/Menu/BaseMenu';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { GameObjectEvent } from 'GameObject/GameObject';
import { UIActions } from 'State/UI/UIActions';
import {
    createEventOfferTimer,
    eventHasOffer,
    getActiveEventLayout,
    getEventOfferData,
} from 'State/Events/EventHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import Game from 'Game';
import Timer from 'System/Timer/Timers/Timer';
import TextComponent from 'Component/Render/Text';
import { PlayerActions } from 'State/Player/PlayerActions';
import LevelHelper from 'Utility/LevelHelper';
import { formatStrikePrice, parseCurrency } from 'Utility/Currency';
import { report } from 'report';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { OfferType } from 'Utility/Helpers/IAP/Offers/OfferTypes/OfferType';
import { ProductId } from 'Utility/Product';
import { unexpectedValue } from 'Utility/Types';
import { LanguageKey } from 'Utility/SDK/Language';
import { IAPCatalog } from 'Utility/SDK/Drivers/IAPCatalog';
import { IAPReward } from 'Utility/RewardHelper';
import Interactable from 'Component/Interactable';
import { GameActions } from 'State/Game/GameActions';
import { ContextType } from 'State/Game/ContextType';

interface IEventOfferShowParams {
    eventType: EventType;
}

export class EventOfferHandler extends BaseMenu {
    private layout: any;
    private timer: Timer;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.EVENT_OFFER, uiSystem, false);
    }

    public async preload() {
        this.resources.set('IAPRewards', `resource/data/rewards/IAPRewards.json`);
        await super.preload();
    }

    protected onCreate(): void {
        super.onCreate();
        this.layout = getActiveEventLayout(PopupID.EVENT_OFFER);
    }

    public onShow(params: IEventOfferShowParams): void {
        super.onShow(params);

        if (!this.layout) {
            this.reportAndClose('Event offer is missing layout', params.eventType, {
                fingerprint: 'EventOfferHandler:EventMissingOfferLayout',
            });
        }

        if (!eventHasOffer(params.eventType)) {
            this.reportAndClose('Event offer menu showed for an event without an offer.', params.eventType, {
                fingerprint: 'EventOfferHandler:EventMissingOfferData',
            });
            return;
        }

        const state = Game.store.getState();
        const offerData = getEventOfferData(params.eventType);
        
        let offerTier = IAPHelper.getRelevantOfferTier(state, offerData);
        // Save spender tier for scalable offer the first time it's shown to the player
        if (
            !state.playerState.currentEventOffer ||
            state.playerState.currentEventOffer?.type !== params.eventType
        ) {
                offerTier = IAPHelper.getRelevantOfferTier(state, offerData);
                PlayerActions.setCurrentEventOffer({
                    type: params.eventType,
                    tier: offerTier
                });
        }
        else offerTier = state.playerState.currentEventOffer.tier;

        if (!offerTier) {
            report('Failed to find relevant offer for event.', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    eventType: params.eventType,
                },
                fingerprint: 'EventOfferHandler:OnShow:FailedToFindRelevantOfferTier',
            });
            UIActions.hidePopup(PopupID.EVENT_OFFER);
            return;
        }

        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        const { closeButton, timeText } = this.layout;

        applyDefaultButtonAnimation(closeButton);

        this.addListener(closeButton, GameObjectEvent.SELECTED, () => UIActions.hidePopup(this.name), { once: true });

        const timerTextComponent = timeText.getComponent(TextComponent);
        this.timer = createEventOfferTimer(state, params.eventType, timerTextComponent, {
            onComplete: () => {
                UIActions.hidePopup(this.name);
            },
        });

        this.timer.start();

        this.cleanupManager.add(() => {
            this.timer.stop();
        });

        const catalog = Game.sdk.getCatalog();
        const language = Game.sdk.getLanguage();

        switch (offerTier.type) {
            case OfferType.SINGLE:
                this.setupProduct(catalog, language, offerTier.productId);
                break;
            case OfferType.MULTI:
                offerTier.products.forEach(product => this.setupProduct(catalog, language, product.productId));
                break;
            default:
                unexpectedValue(offerTier, false);
        }
    }

    private setupProduct(catalog: IAPCatalog, language: LanguageKey, productId: ProductId) {
        const product = catalog.products.find(e => e.productId === productId);
        if (!product) {
            report('Failed to find event offer product', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    productId,
                },
                fingerprint: 'EventOfferHandler:onShow:FailedToFindProduct',
            });
            UIActions.hidePopup(PopupID.EVENT_OFFER);
            return;
        }

        const iapReward = IAPHelper.CACHED_IAP_REWARDS_MAP()[productId];

        const { beforeValue, value, afterValue } = parseCurrency(product.price);

        const fullCostStr = value.toLocaleString(language, { maximumFractionDigits: 2, minimumFractionDigits: 2 });

        this.layout.priceText.getComponent(TextComponent).text = `${beforeValue}${fullCostStr}${afterValue}`;

        const levelCostFactor = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted()) / 1000;
        const strikePriceUsd = iapReward.usdPriceWithoutDiscount;
        const strikePrice = formatStrikePrice({
            price: product.price,
            priceUsd: iapReward.usdPrice,
            strikePriceUsd,
        });


        this.layout.oldPrice.getComponent(TextComponent).text = strikePrice;

        this.setRewardTexts(iapReward);

        // Add listener to button
        this.layout.buyButton.addComponent(Interactable);
        applyDefaultButtonAnimation(this.layout.buyButton);
        this.addListener(this.layout.buyButton, GameObjectEvent.SELECTED, () => {
            GameActions.setContext(ContextType.PURCHASE_PRODUCT, {
                product: iapReward,
            });
        });

        return;
    }

    private setRewardTexts(iapReward: IAPReward): void {
        const jokerReward = iapReward.rewards.find(reward => reward.type === RewardType.JOKER);
        if (jokerReward) this.layout.jokerText.getComponent(TextComponent).text = 'x' + jokerReward.value;
    
        const plusFiveReward = iapReward.rewards.find(reward => reward.type === RewardType.PLUS_FIVE_CARDS);
        if (plusFiveReward) this.layout.plusFiveText.getComponent(TextComponent).text = 'x' + plusFiveReward.value;

        const freePlayReward = iapReward.rewards.find(reward => reward.type === RewardType.FREE_PLAY);
        if (freePlayReward) this.layout.freePlayText.getComponent(TextComponent).text = 'x' + freePlayReward.value;
    }

    private setPrices(productId: ProductId, price: string, strikePrice: string) {}

    private reportAndClose(message: string, eventType: EventType, options?: any) {
        report(message, {
            once: true,
            sampleRate: 0.1,
            tags: {
                eventType: eventType,
            },
            fingerprint: 'EventOfferHandler',
            ...(options ?? {}),
        });
        UIActions.hidePopup(PopupID.EVENT_OFFER);
    }

    private addListener(
        obj: GameObject,
        event: string,
        action: () => any,
        options?: {
            once?: boolean;
        }
    ) {
        if (options?.once) {
            obj.once(event, action);
        } else {
            obj.on(event, action);
        }
        this.cleanupManager.add(() => {
            obj.off(event, action);
        });
    }
}
